import type { DamageReportDto, DamageReportUrgency } from '../../services/damage-reports.api';
import { authorizedFetch } from '../../services/core.api';
import { i18nService, t } from '../../services/i18n';
import { escapeHtml } from './property-shared';

function resolveUrgency(value?: DamageReportUrgency): DamageReportUrgency {
  return value || 'Normal';
}

export class RcPropertyDamageReportsTab extends HTMLElement {
  reports: DamageReportDto[] = [];

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
  }

  render(): void {
    this.innerHTML = this.reports.length === 0
      ? `<div class="empty-state"><p>${t('damageReports.propertyEmpty')}</p></div>`
      : `
        <table>
          <thead>
            <tr>
              <th>${t('common.date')}</th>
              <th>${t('common.tenant')}</th>
              <th>${t('common.description')}</th>
              <th>${t('common.photos')}</th>
            </tr>
          </thead>
          <tbody>
            ${this.reports.map((report) => `
              <tr>
                <td>${escapeHtml(i18nService.formatDate(report.createdAt, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}</td>
                <td>${escapeHtml(report.tenantName || '-')}</td>
                <td>
                  <div class="doc-filename">${escapeHtml(report.propertyAddress)}</div>
                  <div class="text-muted text-sm">${escapeHtml(t(`damageReports.urgency.${resolveUrgency(report.urgency).toLowerCase()}`))}</div>
                  <div class="text-muted text-sm">${escapeHtml(report.description)}</div>
                </td>
                <td>
                  ${report.photos.length === 0 ? '-' : `
                    <div style="display:flex;flex-direction:column;gap:0.35rem;">
                      ${report.photos.map((photo) => `
                        <button class="btn btn-outline btn-sm" data-photo-url="${escapeHtml(photo.downloadUrl)}" data-file-name="${escapeHtml(photo.fileName)}">${escapeHtml(photo.fileName)}</button>
                      `).join('')}
                    </div>
                  `}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
  }

  private handleClick = async (event: Event): Promise<void> => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-photo-url]');
    if (!button?.dataset.photoUrl || !button.dataset.fileName) {
      return;
    }

    const response = await authorizedFetch(button.dataset.photoUrl);
    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
}

if (!customElements.get('rc-property-damage-reports-tab')) {
  customElements.define('rc-property-damage-reports-tab', RcPropertyDamageReportsTab);
}
